import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { teacherNotifications, markNotifReviewed } from "@/lib/api";

/**
 * Compact bell + side-Sheet that lists unreviewed teacher notifications.
 * Replaces the old right-side alert column on the dashboard.
 */
export default function TeacherNotifBell({ teacherId }) {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    if (!teacherId) return;
    try { setNotifs(await teacherNotifications(teacherId)); } catch { /* noop */ }
  };

  useEffect(() => { refresh(); }, [teacherId]); // eslint-disable-line

  const onReview = async (id) => {
    await markNotifReviewed(id);
    refresh();
  };

  const unread = notifs.filter(n => !n.is_reviewed).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="teacher-notif-bell"
          className="relative w-10 h-10 rounded-full bg-white border border-slate-200 hover:border-slate-400 flex items-center justify-center transition"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 && (
            <span
              data-testid="teacher-notif-badge"
              className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
            >
              {unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-teacher">Notifications</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3" data-testid="teacher-notif-list">
          {notifs.slice(0, 30).map(n => (
            <div
              key={n.notif_id}
              data-testid={`notif-row-${n.notif_id}`}
              className={`rounded-xl p-3 border ${n.is_reviewed ? "border-slate-200 bg-slate-50" : "border-amber-300 bg-amber-50"}`}
            >
              <div className="text-sm text-slate-800">
                <strong>{n.learner_name}</strong> was rerouted from <strong>{n.sub_topic}</strong> in {n.subject}.
              </div>
              <div className="text-xs text-slate-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              <div className="flex justify-between items-center mt-2 gap-2">
                <Link
                  to={`/teacher/alert/${n.stop_id}`}
                  className="text-xs font-semibold text-sky-700 hover:underline"
                  onClick={() => setOpen(false)}
                >
                  View detail →
                </Link>
                {!n.is_reviewed && (
                  <Button size="sm" variant="outline" onClick={() => onReview(n.notif_id)} className="rounded-lg text-xs h-7 px-3">
                    Mark reviewed
                  </Button>
                )}
              </div>
            </div>
          ))}
          {notifs.length === 0 && (
            <div className="text-center text-slate-400 py-8" data-testid="teacher-notif-empty">
              No alerts. All quiet.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
